import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useNavUsage } from '../hooks/useNavUsage';
import { useVisibleNavItems } from '../hooks/useVisibleNavItems';
import {
  Menu,
  User,
  Settings,
  LogOut,
  Bell,
  Search,
  HelpCircle,
  Mail,
  FileText,
  AlertCircle,
  CheckCircle,
  X,
} from 'lucide-react';
import { HeaderQuickNav } from './HeaderQuickNav';
import ConfirmPopup from './PurchaseProgram/ConfirmPopup';

const CATEGORY_META = {
  order_request:  { label: 'Order',        color: 'success',  Icon: CheckCircle },
  quote_request:  { label: 'Quote',         color: 'primary',  Icon: FileText },
  needs_review:   { label: 'Needs Review',  color: 'warning',  Icon: AlertCircle },
  blocked:        { label: 'Blocked',       color: 'danger',   Icon: AlertCircle },
  other:          { label: 'Other',         color: 'secondary',Icon: Mail },
};

function formatRelativeTime(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const Header = ({ onToggleSidebar = () => {}, isSidebarOpen = false }) => {
  const { account, logout, getAuthHeaders } = useAuth();
  const { notifications, unreadCount, markRead, markAllRead, removeNotification } = useNotifications();
  const { visibleItems } = useVisibleNavItems();
  const { headerQuickSlots, moreItems } = useNavUsage(visibleItems);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [logoValid, setLogoValid] = useState(true);
  const [resolvedLogoUrl, setResolvedLogoUrl] = useState('');
  const notifPanelRef = useRef(null);
  const notifBtnRef = useRef(null);
  const navigate = useNavigate();

  const toggleUserMenu = () => setShowUserMenu(!showUserMenu);
  const toggleNotifications = () => {
    setShowNotifications((prev) => !prev);
  };

  useEffect(() => {
    if (!showNotifications) return;
    const handleClick = (e) => {
      if (
        notifPanelRef.current && !notifPanelRef.current.contains(e.target) &&
        notifBtnRef.current && !notifBtnRef.current.contains(e.target)
      ) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showNotifications]);
  const normalizeLogoUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/')) return `${API_BASE_URL}${url}`;
    return url;
  };

  const logoUrl =
    resolvedLogoUrl ||
    account?.logo_url ||
    account?.logoUrl ||
    account?.branding?.logo_url ||
    account?.branding?.logoUrl ||
    '';
  const displayLogoUrl = normalizeLogoUrl(logoUrl);

  useEffect(() => {
    setLogoValid(true);
  }, [logoUrl]);

  useEffect(() => {
    const headers = getAuthHeaders();
    if (!headers || Object.keys(headers).length === 0) return;
    if (account?.logo_url || account?.logoUrl) {
      setResolvedLogoUrl(account.logo_url || account.logoUrl || '');
      return;
    }
    const fetchLogo = async () => {
      try {
        const resp = await fetch(`${API_BASE_URL}/api/v1/config/general`, {
          headers
        });
        if (!resp.ok) return;
        const data = await resp.json();
        const config = data?.config || {};
        const branding = config?.branding || {};
        const url = branding.logo_url || branding.logoUrl || config.logo_url || config.logoUrl || '';
        if (url) {
          setResolvedLogoUrl(url);
        }
      } catch (error) {
        setResolvedLogoUrl('');
      }
    };
    fetchLogo();
  }, [account, getAuthHeaders]);

  const handleLogout = () => {
    setShowUserMenu(false);
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    logout();
  };

  return (
    <header className="bg-white border-bottom shadow-sm" style={{ zIndex: 1000 }}>
      <div className="container-fluid">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 gap-lg-3 py-3 position-relative">
          {/* Left side - Logo and breadcrumb */}
          <div className="d-flex align-items-center flex-wrap gap-3">
            <button
              className="btn btn-outline-primary d-inline-flex d-lg-none"
              onClick={onToggleSidebar}
              aria-label={isSidebarOpen ? 'Close navigation menu' : 'Open navigation menu'}
            >
              <Menu size={18} />
            </button>
            <Link to="/dashboard" className="me-1 text-decoration-none d-flex align-items-center gap-2">
              <img
                src="/logo.png"
                alt="AI Tech Assistant Logo"
                style={{ height: '54px', width: 'auto', objectFit: 'contain' }}
                className="d-inline-block align-top"
              />
            </Link>
          </div>

          <HeaderQuickNav slots={headerQuickSlots} moreItems={moreItems} />

          {/* Right side - Search, notifications, user menu */}
          <div className="d-flex align-items-center gap-3 flex-wrap justify-content-end flex-grow-1">
            {displayLogoUrl && logoValid && (
              <img
                src={displayLogoUrl}
                alt="Account Logo"
                style={{ height: '40px', width: 'auto', objectFit: 'contain' }}
                className="d-none d-md-inline-block"
                onError={() => setLogoValid(false)}
              />
            )}
            {/* Search */}
            <div className="position-relative d-none d-lg-block">
              <Search className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" size={18} />
              <input
                type="text"
                className="form-control ps-4"
                placeholder="Search..."
                style={{ width: '250px' }}
              />
            </div>

            {/* Notifications */}
            <div className="position-relative">
              <button
                ref={notifBtnRef}
                className="btn btn-outline-secondary position-relative"
                onClick={toggleNotifications}
                aria-expanded={showNotifications}
                aria-label="Notifications"
              >
                <Bell size={18} />
                {unreadCount > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
            </div>

            {/* Help */}
            <button className="btn btn-outline-secondary d-none d-sm-inline-flex" title="Help">
              <HelpCircle size={18} />
            </button>

            {/* User menu */}
            <div className="position-relative">
              <button
                className="btn btn-outline-primary d-flex align-items-center gap-2"
                onClick={toggleUserMenu}
                aria-expanded={showUserMenu}
                aria-label="User menu"
              >
                <User size={18} />
                <span className="d-none d-md-inline">
                  {account?.username || account?.name || 'User'}
                </span>
                <Menu size={16} />
              </button>

              {/* User dropdown menu */}
              {showUserMenu && (
                <div className="dropdown-menu show position-absolute end-0 mt-2" style={{ minWidth: '200px', zIndex: 1001 }}>
                  <div className="dropdown-header d-flex flex-column gap-0">
                    <div className="fw-bold">{account?.name || 'User'}</div>
                    {account?.username && (
                      <div className="text-muted" style={{ fontSize: '0.8em' }}>{account.username}</div>
                    )}
                  </div>
                  <div className="dropdown-divider"></div>
                  
                  <>
                    <Link to="/configurations" className="dropdown-item d-flex align-items-center gap-2">
                      <Settings size={16} />
                      Settings
                    </Link>
                    <div className="dropdown-divider"></div>
                  </>
                  
                  <button
                    className="dropdown-item d-flex align-items-center gap-2 text-danger"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showNotifications && (
        <div className="header-notifications-panel" ref={notifPanelRef}>
          <div className="container-fluid">
            <div className="header-notifications-card">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                  Notifications
                  {unreadCount > 0 && (
                    <span className="badge bg-danger ms-2" style={{ fontSize: '0.7em' }}>{unreadCount}</span>
                  )}
                </h6>
                {unreadCount > 0 && (
                  <button
                    className="btn btn-sm btn-link text-decoration-none p-0"
                    onClick={markAllRead}
                  >
                    Mark all read
                  </button>
                )}
              </div>

              <div className="border-top py-1" style={{ maxHeight: '360px', overflowY: 'auto' }}>
                {notifications.length === 0 ? (
                  <div className="text-center text-muted py-3 small">No notifications</div>
                ) : (
                  notifications.slice(0, 20).map((n) => {
                    const meta = CATEGORY_META[n.category] || CATEGORY_META.other;
                    const Icon = meta.Icon;
                    return (
                      <div
                        key={n.id}
                        className={`d-flex align-items-start py-2 px-1 notif-row${n.read ? '' : ' notif-unread'}`}
                        style={{ cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                        onClick={() => {
                          markRead(n.id);
                          if (n.linkTo) {
                            navigate(n.linkTo);
                            setShowNotifications(false);
                          }
                        }}
                      >
                        <div className={`bg-${meta.color} rounded-circle p-1 me-2 flex-shrink-0`} style={{ marginTop: '2px' }}>
                          <Icon size={11} className="text-white" />
                        </div>
                        <div className="flex-grow-1 overflow-hidden">
                          <div className="small fw-semibold text-truncate" style={{ maxWidth: '240px' }}>
                            {n.title}
                          </div>
                          {n.body && (
                            <div className="small text-muted text-truncate" style={{ maxWidth: '240px' }}>{n.body}</div>
                          )}
                          <div className="d-flex align-items-center gap-2 mt-1">
                            <span className={`badge bg-${meta.color} bg-opacity-10 text-${meta.color}`} style={{ fontSize: '0.65em' }}>
                              {meta.label}
                            </span>
                            <span className="text-muted" style={{ fontSize: '0.7em' }}>
                              {formatRelativeTime(n.timestamp)}
                            </span>
                          </div>
                        </div>
                        <button
                          className="btn btn-link btn-sm p-0 ms-1 text-muted flex-shrink-0"
                          style={{ lineHeight: 1 }}
                          onClick={(e) => { e.stopPropagation(); removeNotification(n.id); }}
                          aria-label="Dismiss"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="pt-2">
                <Link
                  to="/order-x/email-inbox"
                  className="btn btn-outline-primary btn-sm w-100"
                  onClick={() => setShowNotifications(false)}
                >
                  Open OrderX Inbox
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmPopup
        open={showLogoutConfirm}
        title="Log out?"
        message="You'll be signed out here, and any other open tabs signed in with this account will be signed out too — tabs opened automatically will close, others will return to where you came from."
        confirmLabel="Log out"
        variant="danger"
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />

      {/* Click outside to close dropdowns */}
      {showUserMenu && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ zIndex: 999 }}
          onClick={() => {
            setShowUserMenu(false);
          }}
        />
      )}

      <style>{`
        .header-notifications-panel {
          border-top: 1px solid #e2e8f0;
          background: #ffffff;
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
        }
        .header-notifications-card {
          padding: 16px 0;
        }
        .header-notifications-panel .border-top {
          border-color: #e2e8f0 !important;
        }
        .notif-row:hover {
          background: #f8fafc;
          border-radius: 6px;
        }
        .notif-unread {
          background: #eff6ff;
        }
        .notif-unread:hover {
          background: #dbeafe;
        }
      `}</style>
    </header>
  );
};

export default Header;
